import { React, useEffect, useRef, useState } from "react";
import { Box, Button, Typography, Container, Snackbar, Alert } from "@mui/material";
import { useNavigate } from "react-router-dom";
import StepProgress from './StepProgress';
import { useAuth } from "../context/AuthContext";
import gb from '../assets/images/gbottom.png';
import CircularProgress from "@mui/material/CircularProgress";

import { motion } from "framer-motion";
import NotLoggedIn from "../components/LoginExceptions";
import { REST_URI,RAZORPAY_KEY } from '../graphql/client';

const PaymentScreen = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const paymentContainer = useRef(null);
    const [orderId, setOrderId] = useState(null);
    const [finalAmount, setFinalAmount] = useState(null);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [loadingPayment, setLoadingPayment] = useState(false);
    const [paymentStarted, setPaymentStarted] = useState(false);
    const [redirecting, setRedirecting] = useState(false);

    const storedUserType = localStorage.getItem("user_type");
    const paymentCompleted = localStorage.getItem("payment_completed") === "true";

    const handleCloseSnackbar = () => {
        setOpenSnackbar(false);
    };

    // Define a function to force redirect to profile
    const forceRedirectToProfile = () => {
        console.log("Forcing redirect to profile");
        // Try multiple redirection methods to ensure it works
        try {
            navigate("/profile");
            setTimeout(() => {
                window.location.href = "/profile";
            }, 100);
        } catch (e) {
            console.error("Navigation error:", e);
            window.location.href = "/profile";
        }
    };

    // Check if payment is already completed and redirect to profile
    useEffect(() => {
        // Check if payment is completed immediately when component mounts
        if (paymentCompleted) {
            console.log("Payment completed flag found - redirecting");
            setRedirecting(true);
            setOpenSnackbar(true);
            
            // Force redirect after a brief delay
            setTimeout(forceRedirectToProfile, 500);
        }
    }, [paymentCompleted]);

    const fetchOrder = async () => {
        setLoadingPayment(true); // Start loading
        setPaymentStarted(true); // Set payment process has started - button will disappear

        try {
            const amount = 2000;
            const mobile = localStorage.getItem("mobile");

            // Making a call to your backend to create the Razorpay order
            const response = await fetch(`${REST_URI}/create-order`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ amount, mobile }) // Send amount for the order creation
            });

            if (!response.ok) {
                throw new Error(`Server Error: ${response.status}`);
            
            }
            setLoadingPayment(false);

            const orderData = await response.json();
            console.log("Order Data:", orderData);

            if (!orderData.orderId || !orderData.amount) {
                throw new Error("Invalid order response");
            }

            // Set orderId and amount (in paise)
            setOrderId(orderData.orderId);
            setFinalAmount(orderData.amount);
        } catch (error) {
            console.error("Error fetching order:", error);
            alert("Failed to create order. Please try again.");
            setLoadingPayment(false);
            setPaymentStarted(false); // Reset if there's an error so user can try again
        }
    };

    useEffect(() => {
        const mobile = localStorage.getItem("mobile");
    
        if (orderId && finalAmount && paymentContainer.current) {
            const options = {
                key: RAZORPAY_KEY,
                amount: finalAmount,
                currency: "INR",
                name: "TKS Trust",
                description: "UPI QR Payment",
                order_id: orderId,
                image: "/CWA_logo.png",
                theme: {
                    color: "#FFA500",
                    hide_topbar: false,
                },
                modal: {
                    backdropclose: false,
                    confirm_close: true,
                    animation: true,
                    ondismiss: function() {
                        console.log("Payment cancelled by user");
                        setPaymentStarted(false); // Reset so user can try again
                        setLoadingPayment(false);
                        setOrderId(null); // Clear order ID so new one is created
                        setFinalAmount(null);
                    }
                },
                handler: async function (response) {
                    console.log("Payment Successful:", response);
                    localStorage.setItem("payment_completed", "true");
    
                    const mobile = localStorage.getItem("mobile");
                    const verifyParams = new URLSearchParams({
                        paymentId: response.razorpay_payment_id,
                        orderId: orderId,
                    }).toString();
    
                    try {
                        const verifyResponse = await fetch(`${REST_URI}/verify-payment`, {
                            method: "POST",
                            headers: {
                                "Content-Type": "application/json",
                            },
                            body: JSON.stringify({
                                paymentId: response.razorpay_payment_id,
                                orderId: orderId,
                                signature: response.razorpay_signature,
                            }),
                        });
    
                        const verifyData = await verifyResponse.json();
                        setSnackbarMessage(verifyData.message);
                        setSnackbarOpen(true);
                        localStorage.setItem("payment_completed", "true");
                        
                        setTimeout(() => {
                            navigate('/receipt');
                            window.scrollTo(0, 0);
                        }, 500);
                    } catch (error) {
                        console.error("Error verifying payment:", error);
                        setSnackbarMessage("Payment verification failed.");
                        setSnackbarOpen(true);
                        setPaymentStarted(false); // Reset payment started if verification fails
                    } finally {
                        setLoadingPayment(false);
                    }
                },
                // Handle payment failure
                failure: function (response) {
                    console.log("Payment failed:", response);
                    setSnackbarMessage("Payment failed. Please try again.");
                    setSnackbarOpen(true);
                    setPaymentStarted(false); // Reset so user can try again
                    setLoadingPayment(false);
                    setOrderId(null); // Clear order ID so new one is created
                    setFinalAmount(null);
                },
                payment_capture: 1,
                method: {
                    upi: true,
                    netbanking: true,
                    card: true,
                },
                upi: {
                    flow: "collect",
                },
                prefill: {
                    contact: mobile,
                },
                container: paymentContainer.current,
            };
    
            const razorpay = new window.Razorpay(options);
            razorpay.open();
        }
    }, [orderId, finalAmount]);

    if (!storedUserType) {
        return <NotLoggedIn />;
    }
    
    // Show loading state during redirection
    if (paymentCompleted && redirecting) {
        return (
            <Box
                sx={{
                    minHeight: "100vh",
                    background: "#f8f9e9",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    px: 2,
                }}
            >
                <Container maxWidth="sm">
                    <Box
                        sx={{
                            backdropFilter: "blur(10px)",
                            backgroundColor: "#f8f9e9",
                            boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
                            borderRadius: 4,
                            p: 4,
                            textAlign: "center",
                        }}
                    >
                        <CircularProgress sx={{ color: "#28a745", mb: 2 }} />
                        <Typography variant="h6" sx={{ color: "#2A289C" }}>
                            Payment already completed!
                        </Typography>
                        <Typography sx={{ mt: 2, color: "#555" }}>
                            Redirecting to your profile...
                        </Typography>
                    </Box>
                </Container>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: "100vh",
                background: "#f8f9e9",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                px: 2,
                position: "relative",
                overflow: "hidden",
            }}
        >
            <Container maxWidth="sm">
                <motion.div
                    initial={{ opacity: 0, y: 60 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                >
                    <Box
                        sx={{
                            backdropFilter: "blur(10px)",
                            backgroundColor: "#f8f9e9",
                            boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
                            borderRadius: 4,
                            p: 4,
                            textAlign: "center",
                        }}
                    >

                        <Typography
                            variant="h5"
                            sx={{ fontWeight: "bold", color: "#2A289C", mb: 2 }}
                        >
                            One-Time Payment of ₹2000
                        </Typography>
                        <Typography sx={{ mb: 4, color: "#555" }}>
                            Your payment is safe and secure. Please complete the transaction to
                            proceed.
                        </Typography>

                        {!paymentStarted && (
                            <motion.div
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.97 }}
                                transition={{ type: "spring", stiffness: 300 }}
                            >
                                <Button
                                    variant="contained"
                                    onClick={fetchOrder}
                                    disabled={loadingPayment}
                                    sx={{
                                        background: "linear-gradient(90deg, #28a745, #218838)",
                                        px: 4,
                                        py: 1.5,
                                        fontWeight: "bold",
                                        borderRadius: '999px',
                                        fontSize: "16px",
                                        "&:hover": {
                                            background: "linear-gradient(90deg, #218838, #1e7e34)",
                                        },
                                    }}
                                >
                                    {loadingPayment ? <CircularProgress size={24} color="inherit" /> : "Proceed to Pay"}
                                </Button>
                            </motion.div>
                        )}

                        {paymentStarted && loadingPayment && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                                <CircularProgress size={32} sx={{ color: '#28a745' }} />
                                <Typography sx={{ ml: 2, color: '#555' }}>
                                    Initializing payment...
                                </Typography>
                            </Box>
                        )}

                        <Box
                            ref={paymentContainer}
                            id="razorpay-container"
                            sx={{ mt: 2, width: "100%", height: "0px" }}
                        />

                        <Typography
                            variant="body2"
                            sx={{ mt: 4, color: "#7A7841", fontSize: "14px" }}
                        >
                            For transactions other than ₹2000, please contact TKS Admin at
                            <br />
                            <a href="tel:+919841818740" style={{ color: "#7A7841",fontWeight: "14px", textDecoration: "bold" }}>
                            +91 9841818740
                            </a> or {" "}
                            <a href="mailto:finance@tkstrust.com">finance@tkstrust.com</a>
                        </Typography>

                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.3 }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    gap: 2,
                                    mt: 4,
                                }}
                            >
                                <Button
                                    variant="contained"
                                    onClick={() => {navigate("/profile");window.scrollTo(0, 0);}}
                                    sx={{
                                        backgroundColor: "#6C757D",
                                        borderRadius: '999px',
                                        color: "#fff",
                                        fontWeight: "bold",
                                        "&:hover": {
                                            backgroundColor: "#5a6268",
                                        },
                                    }}
                                >
                                    Back
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={() => {navigate("/");window.scrollTo(0, 0);}}
                                    sx={{
                                        backgroundColor: "#E52029",
                                        borderRadius: '999px',
                                        color: "#fff",
                                        fontWeight: "bold",
                                        "&:hover": {
                                            backgroundColor: "#c71c23",
                                        },
                                    }}
                                >
                                    Cancel
                                </Button>
                            </Box>
                        </motion.div>
                    </Box>
                </motion.div>

                {/* Snackbar Alert */}
                <Snackbar
                    open={openSnackbar}
                    autoHideDuration={3000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: "top", horizontal: "center" }}
                >
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                    >
                        <Alert
                            onClose={handleCloseSnackbar}
                            severity={paymentCompleted ? "info" : "success"}
                            sx={{
                                width: "100%",
                                fontSize: "16px",
                                fontWeight: "bold",
                                boxShadow: "0 0 12px rgba(0,0,0,0.1)",
                            }}
                        >
                            {paymentCompleted ? "Payment already completed. Redirecting to profile..." : "Payment Completed Successfully!"}
                        </Alert>
                    </motion.div>
                </Snackbar>
            </Container>

            {/* Decorative Footer Image */}
            <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: "easeOut", delay: 0.5 }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        width: { xs: "120px", sm: "400px" },
                        height: { xs: "40px", sm: "150px" },
                        backgroundImage: `url(${gb})`,
                        backgroundRepeat: "no-repeat",
                        backgroundSize: "contain",
                        backgroundPosition: "left bottom",
                    }}
                />
            </motion.div>
        </Box>
    );
};
export default PaymentScreen;